import React, { useEffect, useRef, useCallback } from 'react';

/**
 * Leaflet map: red = pickup yards, blue = delivery site.
 * Loads Leaflet from CDN (same pattern as ProjectDetails).
 */
function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.L) {
      resolve(window.L);
      return;
    }
    if (document.querySelector('script[src*="leaflet.js"]')) {
      const check = () => {
        if (window.L) resolve(window.L);
        else setTimeout(check, 50);
      };
      check();
      return;
    }
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      css.crossOrigin = '';
      document.head.appendChild(css);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.crossOrigin = '';
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Leaflet failed to load'));
    document.head.appendChild(script);
  });
}

const popupCoords = (lat, lng) =>
  `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;

const googleMapsHref = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return '#';
  return `https://www.google.com/maps?q=${encodeURIComponent(`${la},${ln}`)}`;
};

/**
 * Card-style popup: title, optional subtitle (e.g. event · time), coords, primary link.
 */
function buildPopupHtml({ title, subtitle, lat, lng }) {
  const coords = popupCoords(lat, lng);
  const href = googleMapsHref(lat, lng);
  const sub =
    subtitle && String(subtitle).trim()
      ? `<div class="deliveries-map-popup__subtitle">${escapeHtml(String(subtitle).trim())}</div>`
      : '';
  return (
    `<div class="deliveries-map-popup">` +
    `<div class="deliveries-map-popup__title">${escapeHtml(title)}</div>` +
    sub +
    `<div class="deliveries-map-popup__coords">${escapeHtml(coords)}</div>` +
    `<a class="deliveries-map-popup__link" href="${href}" target="_blank" rel="noopener noreferrer">Open in Google Maps</a>` +
    `</div>`
  );
}

const DeliveriesRouteMap = ({ show, pickupPins, deliveryPin, actualPickupPin, actualDeliveryPin }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  const initMap = useCallback(async () => {
    const el = containerRef.current;
    if (!el || !show) return;

    const L = await loadLeaflet().catch(() => null);
    if (!L || !containerRef.current) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const pickups = (pickupPins || []).filter((p) => p.lat != null && p.lng != null);
    const del = deliveryPin && deliveryPin.lat != null && deliveryPin.lng != null ? deliveryPin : null;
    const actPickup =
      actualPickupPin && actualPickupPin.lat != null && actualPickupPin.lng != null ? actualPickupPin : null;
    const actDrop =
      actualDeliveryPin && actualDeliveryPin.lat != null && actualDeliveryPin.lng != null ? actualDeliveryPin : null;

    const points = [
      ...pickups.map((p) => [p.lat, p.lng]),
      ...(del ? [[del.lat, del.lng]] : []),
      ...(actPickup ? [[actPickup.lat, actPickup.lng]] : []),
      ...(actDrop ? [[actDrop.lat, actDrop.lng]] : [])
    ];

    if (points.length === 0) {
      el.innerHTML = '';
      return;
    }

    const map = L.map(el, { scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(map);

    const bounds = L.latLngBounds(points);

    pickups.forEach((p) => {
      L.circleMarker([p.lat, p.lng], {
        radius: 8,
        color: '#b91c1c',
        weight: 2,
        fillColor: '#ef4444',
        fillOpacity: 0.95
      })
        .bindPopup(
          buildPopupHtml({
            title: 'Planned pickup',
            subtitle: p.label || 'Yard',
            lat: p.lat,
            lng: p.lng
          }),
          { maxWidth: 280 }
        )
        .addTo(map);
    });

    if (del) {
      L.circleMarker([del.lat, del.lng], {
        radius: 8,
        color: '#1d4ed8',
        weight: 2,
        fillColor: '#3b82f6',
        fillOpacity: 0.95
      })
        .bindPopup(
          buildPopupHtml({
            title: 'Planned delivery',
            subtitle: del.label || 'Site',
            lat: del.lat,
            lng: del.lng
          }),
          { maxWidth: 280 }
        )
        .addTo(map);
    }

    if (actPickup) {
      L.circleMarker([actPickup.lat, actPickup.lng], {
        radius: 9,
        color: '#0f172a',
        weight: 2,
        fillColor: '#22c55e',
        fillOpacity: 0.95
      })
        .bindPopup(
          buildPopupHtml({
            title: 'Actual pickup',
            subtitle: actPickup.label || '',
            lat: actPickup.lat,
            lng: actPickup.lng
          }),
          { maxWidth: 280 }
        )
        .addTo(map);
    }

    if (actDrop) {
      L.circleMarker([actDrop.lat, actDrop.lng], {
        radius: 9,
        color: '#0f172a',
        weight: 2,
        fillColor: '#a855f7',
        fillOpacity: 0.95
      })
        .bindPopup(
          buildPopupHtml({
            title: 'Actual dropoff',
            subtitle: actDrop.label || '',
            lat: actDrop.lat,
            lng: actDrop.lng
          }),
          { maxWidth: 280 }
        )
        .addTo(map);
    }

    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 12 });
    mapRef.current = map;

    setTimeout(() => map.invalidateSize(), 200);
  }, [show, pickupPins, deliveryPin, actualPickupPin, actualDeliveryPin]);

  useEffect(() => {
    if (!show) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      return;
    }
    initMap();
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [show, initMap]);

  useEffect(() => {
    if (!show || !mapRef.current) return;
    const t = setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 350);
    return () => clearTimeout(t);
  }, [show]);

  const pickups = (pickupPins || []).filter((p) => p.lat != null && p.lng != null);
  const del = deliveryPin && deliveryPin.lat != null && deliveryPin.lng != null;
  const actPickup = actualPickupPin && actualPickupPin.lat != null && actualPickupPin.lng != null;
  const actDrop = actualDeliveryPin && actualDeliveryPin.lat != null && actualDeliveryPin.lng != null;

  if (pickups.length === 0 && !del && !actPickup && !actDrop) {
    return null;
  }

  return (
    <div className="deliveries-route-map">
      <p className="deliveries-route-map__hint small text-muted mb-2">
        <strong>Click a pin</strong> to open a card with time, coordinates, and <strong>Open in Google Maps</strong>.{' '}
        <strong>Planned</strong> stops are red (pickup) and blue (delivery).{' '}
        <strong>Actual</strong> GPS from the driver app is green (pickup) and purple (delivery).
      </p>
      <div className="deliveries-route-map__legend small text-muted mb-2">
        <span className="deliveries-route-map__dot deliveries-route-map__dot--pickup" /> Planned pickup
        <span className="ms-3">
          <span className="deliveries-route-map__dot deliveries-route-map__dot--delivery" /> Planned delivery
        </span>
        <span className="ms-3">
          <span className="deliveries-route-map__dot deliveries-route-map__dot--actual-pickup" /> Actual pickup
        </span>
        <span className="ms-3">
          <span className="deliveries-route-map__dot deliveries-route-map__dot--actual-dropoff" /> Actual delivery
        </span>
      </div>
      <div ref={containerRef} className="deliveries-route-map__canvas" role="img" aria-label="Pickup and delivery locations" />
    </div>
  );
};

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default DeliveriesRouteMap;
